import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { WishlistStore, WishlistUpdateEvent } from '@theme/wishlist-store';

/**
 * A custom element that manages a wishlist drawer.
 * Mirrors the cart drawer pattern from CartDrawerComponent.
 *
 * The badge and drawer content are client-side only (localStorage).
 * Morph/section re-renders reset them to server state (empty).
 * MutationObservers detect these resets and restore the correct state.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class WishlistDrawerComponent extends DialogComponent {
  /** Tracks items recently added to cart (show checkmark instead of cart icon) */
  #addedHandles = new Set();

  /** Watches for morph resetting the badge */
  #badgeObserver = new MutationObserver(() => {
    this.#badgeObserver.disconnect();
    this.#updateBadge();
    this.#watchBadge();
  });

  /** Watches for morph resetting the drawer content (empty state / items) */
  #contentObserver = new MutationObserver(() => {
    this.#contentObserver.disconnect();
    if (this.refs.dialog?.open) {
      this.#render();
    }
    this.#watchContent();
  });

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#handleUpdate);
    this.#updateBadge();
    this.#watchBadge();
    this.#watchContent();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#handleUpdate);
    this.#badgeObserver.disconnect();
    this.#contentObserver.disconnect();
  }

  #watchBadge() {
    const badge = document.querySelector('.wishlist-drawer__badge');
    if (badge) {
      this.#badgeObserver.observe(badge, { attributes: true, childList: true, characterData: true, subtree: true });
    }
  }

  #watchContent() {
    const content = this.refs.dialog?.querySelector('.wishlist-drawer__content');
    if (content) {
      this.#contentObserver.observe(content, { childList: true, subtree: true, attributes: true });
    }
  }

  open() {
    this.#render();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Remove an item from the wishlist.
   * @param {PointerEvent} event
   */
  removeItem(event) {
    event.stopPropagation();
    const button = event.target.closest('[data-handle]');
    if (button) {
      WishlistStore.remove(button.dataset.handle);
    }
  }

  /**
   * Clear all items from the wishlist.
   * @param {PointerEvent} event
   */
  clearAll(event) {
    event.preventDefault();
    event.stopPropagation();
    WishlistStore.clearAll();
  }

  /**
   * Add a single wishlist item to cart.
   * @param {PointerEvent} event
   */
  async addToCart(event) {
    event.stopPropagation();
    const button = event.target.closest('[data-variant-id]');
    if (!button) return;

    const variantId = button.dataset.variantId;
    button.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const cart = await response.json();

      // Show checkmark before dispatching CartAddEvent (which triggers morph/re-render)
      const handle = button.dataset.handle;
      this.#addedHandles.add(handle);
      button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
      button.style.opacity = '1';

      setTimeout(() => {
        this.#addedHandles.delete(handle);
        // Only re-render if dialog is still open (morph might have already done it)
        if (this.refs.dialog?.open) {
          this.#contentObserver.disconnect();
          this.#render();
          this.#watchContent();
        }
      }, 2000);

      this.dispatchEvent(new CartAddEvent(cart, variantId, { source: 'wishlist', itemCount: 1 }));
    } catch (e) {
      console.error('Wishlist add to cart failed:', e);
      button.disabled = false;
    }
  }

  /**
   * Add all wishlist items to cart at once.
   * @param {PointerEvent} event
   */
  async addAllToCart(event) {
    event.stopPropagation();
    const button = event.target.closest('.wishlist-drawer__add-all');
    if (!button) return;

    const items = WishlistStore.items
      .filter((item) => item.variantId)
      .map((item) => ({ id: Number(item.variantId), quantity: 1 }));

    if (items.length === 0) return;

    button.disabled = true;
    button.textContent = 'Wird hinzugefügt…';

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const cart = await response.json();

      this.dispatchEvent(new CartAddEvent(cart, 'wishlist-all', { source: 'wishlist', itemCount: items.length }));

      button.textContent = 'Hinzugefügt ✓';
      setTimeout(() => {
        button.textContent = 'Alle in den Warenkorb';
        button.disabled = false;
      }, 1500);
    } catch (e) {
      console.error('Wishlist add all to cart failed:', e);
      button.textContent = 'Alle in den Warenkorb';
      button.disabled = false;
    }
  }

  #handleUpdate = () => {
    this.#updateBadge();
    // Disconnect content observer before render to avoid re-triggering
    this.#contentObserver.disconnect();
    if (this.refs.dialog?.open) {
      this.#render();
    }
    this.#watchContent();
  };

  #updateBadge() {
    this.#badgeObserver.disconnect();
    const count = WishlistStore.count;

    document.querySelectorAll('.wishlist-drawer__badge').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });

    this.#watchBadge();
  }

  #render() {
    const items = WishlistStore.items;
    const container = this.refs.dialog?.querySelector('.wishlist-drawer__items');
    const emptyState = this.refs.dialog?.querySelector('.wishlist-drawer__empty');
    const headerTitle = this.refs.dialog?.querySelector('.wishlist-drawer__heading');
    const footer = this.refs.dialog?.querySelector('.wishlist-drawer__footer');

    if (!container) return;

    if (items.length === 0) {
      container.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      if (headerTitle) headerTitle.textContent = 'Wunschliste';
      if (footer) footer.hidden = true;
      return;
    }

    if (emptyState) emptyState.hidden = true;
    if (headerTitle) headerTitle.textContent = `Wunschliste (${items.length})`;
    if (footer) footer.hidden = false;

    container.innerHTML = items
      .map(
        (item) => `
      <div class="wishlist-drawer__item">
        ${item.image && !item.image.includes('Liquid error')
          ? `<a href="${item.url}" class="wishlist-drawer__item-image-link">
              <img
                src="${item.image}"
                alt="${item.title}"
                class="wishlist-drawer__item-image"
                width="80"
                height="80"
                loading="lazy"
              />
            </a>`
          : ''}
        <div class="wishlist-drawer__item-details">
          <a href="${item.url}" class="wishlist-drawer__item-title">${item.title}</a>
          <span class="wishlist-drawer__item-price">${item.price}</span>
        </div>
        <div class="wishlist-drawer__item-actions">
          ${item.variantId
            ? this.#addedHandles.has(item.handle)
              ? `<button
                  class="wishlist-drawer__cart-btn button-unstyled"
                  style="opacity:1"
                  disabled
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </button>`
              : `<button
                  class="wishlist-drawer__cart-btn button-unstyled"
                  on:click="/addToCart"
                  data-variant-id="${item.variantId}"
                  data-handle="${item.handle}"
                  aria-label="In den Warenkorb"
                  type="button"
                  title="In den Warenkorb"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
                  </svg>
                </button>`
            : ''}
          <button
            class="wishlist-drawer__item-remove button-unstyled"
            on:click="/removeItem"
            data-handle="${item.handle}"
            aria-label="Entfernen"
            type="button"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
              <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    `
      )
      .join('');
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
